import { useRef, useState } from 'react';
import { Mic, Loader2, Square } from 'lucide-react';
import { toast } from 'sonner';
import { openaiKey, transcribe } from '../lib/media';
import { reportKeyStatus } from '../lib/keyStatus';

const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
const canRecord = typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

/** Tap to talk: OpenAI transcription when an OpenAI key exists, otherwise the device's own dictation. */
export default function VoiceButton({ onText, disabled }) {
  const [state, setState] = useState('idle');
  const ref = useRef(null);
  if (!SR && !canRecord) return null;

  const stop = () => { ref.current?.stop(); };
  const start = async () => {
    const key = canRecord ? await openaiKey() : null;
    if (key) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const rec = new MediaRecorder(stream); const chunks = [];
        rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
        rec.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          setState('busy');
          try {
            const r = await transcribe(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }), key);
            if (r.text) onText(r.text); else toast.message('Non ho sentito nulla');
            if (r.key_status?.low?.length) reportKeyStatus(r.key_status);
          } catch (e) { toast.error(e.message); }
          setState('idle');
        };
        ref.current = rec; rec.start(); setState('rec');
        setTimeout(() => rec.state === 'recording' && rec.stop(), 120000);
      } catch { toast.error('Microfono non disponibile: controlla i permessi'); }
      return;
    }
    if (!SR) return toast.error('Dettatura non supportata: aggiungi una chiave OpenAI in Config');
    const r = new SR(); r.lang = 'it-IT'; r.interimResults = false; r.continuous = true;
    r.onresult = (e) => onText(Array.from(e.results).slice(e.resultIndex).map((x) => x[0].transcript).join(' '));
    r.onerror = (e) => { if (e.error !== 'aborted') toast.error(e.error === 'not-allowed' ? 'Microfono non consentito' : 'Dettatura interrotta'); };
    r.onend = () => setState('idle');
    ref.current = r; r.start(); setState('rec');
  };
  return (
    <button data-testid="architect-voice-btn" type="button" disabled={disabled || state === 'busy'} onClick={state === 'rec' ? stop : start}
      aria-label={state === 'rec' ? 'Ferma registrazione' : 'Parla con l\u2019Architetto'} title={state === 'rec' ? 'Tocca per fermare' : 'Parla'}
      className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-colors disabled:opacity-40 ${state === 'rec' ? 'bg-rose-600 animate-pulse' : 'bg-white/5 hover:bg-white/10 text-slate-300'}`}>
      {state === 'busy' ? <Loader2 className="h-4 w-4 animate-spin" /> : state === 'rec' ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
